import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { TreatmentManager } from '@/components/treatment-manager/TreatmentManager';
import { getCustomers } from '@/services/dataService';
import type { Customer } from '@/services/dataService';
import { usePageTitle } from '@/hooks/usePageTitle';
// import { TreatmentService } from '@/services/treatmentService';

const TreatmentNew: React.FC = () => {
  usePageTitle('施術記録新規作成');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  
  const loadCustomerData = useCallback(async () => {
    try {
      // Issue #237: DataService経由でSupabaseからデータ取得
      const customers = await getCustomers();
      const foundCustomer = customers.find(c => c.id === parseInt(id!));
      if (foundCustomer) {
        setCustomer(foundCustomer);
      } else {
        navigate('/customers');
      }
    } catch (error) {
      console.error('Error loading customer:', error);
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);
  
  useEffect(() => {
    if (id) {
      loadCustomerData();
    }
  }, [id, loadCustomerData]);
  
  const handleBack = () => {
    navigate(`/customers/${id}`);
  };
  
  const handleCancel = () => {
    if (confirm('入力中の内容は保存されません。よろしいですか？')) {
      handleBack();
    }
  };
  
  const handleSave = () => {
    // Save will be handled by TreatmentManager
    // After successful save, navigate back
    setTimeout(() => {
      navigate(`/customers/${id}`);
    }, 100);
  };
  
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }
  
  if (!customer) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>顧客が見つかりません</Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBack} size="large">
              <ArrowBackIcon />
            </IconButton>
            
            <Breadcrumbs>
              <Link 
                component="button"
                variant="body1"
                onClick={() => navigate('/customers')}
                underline="hover"
                color="inherit"
              >
                顧客管理
              </Link>
              <Link 
                component="button"
                variant="body1"
                onClick={handleBack}
                underline="hover"
                color="inherit"
              >
                {customer.name}様
              </Link>
              <Typography color="text.primary">
                新規施術記録
              </Typography>
            </Breadcrumbs>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
            >
              キャンセル
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
            >
              保存
            </Button>
          </Box>
        </Box>
      </Paper>
      
      {/* Main Content */}
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Paper 
          elevation={1}
          sx={{ 
            p: 3,
            borderRadius: 2,
            minHeight: '600px'
          }}
        >
          <Typography variant="h5" sx={{ mb: 3 }}>
            {customer.name}様の新規施術記録
          </Typography>
          
          <TreatmentManager
            customerId={customer.id}
            mode="create"
            initialData={{
              customerName: customer.name,
              staffName: customer.preferredStaff || '',
              date: new Date(),
              physicalCondition: '良好',
              symptoms: [],
              treatmentMenu: '',
              treatmentDuration: 60,
              // Additional properties for TreatmentManager compatibility
            }}
            // onSaveSuccess handled internally by TreatmentManager
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default TreatmentNew;